import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TablePagination,
  FormControl,
  TextField,
  IconButton,
  Select,
  Box,
  Button,
  InputLabel,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Avatar,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CategoryIcon from '@mui/icons-material/Category';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios'; // Import Axios for making API requests
import Swal from 'sweetalert2';

// Project import
import { gridSpacing } from 'config.js';

// Updated API URLs
const apiUrl = 'https://apidashboard.eurekagroup.id/document-categories';
const addCategoryUrl = `${apiUrl}/add`;
const editCategoryUrl = `${apiUrl}/edit`;
const deleteCategoryUrl = (id) => `${apiUrl}/delete/${id}`;

const DocumentCategoryPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [isActive, setIsActive] = useState(1);
  const [errors, setErrors] = useState({}); // Store validation errors

  // Fetch document categories data on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setData(response.data); // Set the fetched data to the state
      } catch (error) {
        console.error('Error fetching document categories data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (category) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryName(category.name);
      setIsActive(category.is_active ? 1 : 0);
    } else {
      setSelectedCategory(null);
      setCategoryName('');
      setIsActive(1);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const validateForm = () => {
    let formErrors = {};
    if (!categoryName.trim()) {
      formErrors.categoryName = 'Category name is required';
    }
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSaveCategory = async () => {
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    // Validate form fields before saving
    if (!validateForm()) {
      return; // If validation fails, stop the submission
    }
  
    const categoryData = { name: categoryName, is_active: isActive === 1 };
  
    if (selectedCategory) {
      // If editing document category
      try {
        await axios.put(editCategoryUrl, { ...categoryData, id: selectedCategory._id }, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData(data.map(cat => (cat._id === selectedCategory._id ? { ...cat, ...categoryData } : cat)));
        setMessage('Document category updated successfully');
      } catch (error) {
        console.error('Error updating document category:', error.response?.data || error.message);
        setMessage(error.response?.data?.error || 'Error updating document category');
      }
    } else {
      // If adding a new document category
      try {
        const response = await axios.post(addCategoryUrl, categoryData, {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        });
        setData([...data, response.data]);
        setMessage('Document category added successfully');
      } catch (error) {
        console.error('Error adding document category:', error.response?.data || error.message);
        setMessage(error.response?.data?.error || 'Error adding document category');
      }
    }
  
    setSuccessPopup(true);
    handleCloseDialog();
  };  

  const handleDeleteClick = async (id) => {
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      try {
        await axios.delete(deleteCategoryUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Tambahkan header Authorization
          },
        }); // Call DELETE API with Authorization header
        setData(data.filter(category => category._id !== id));
        setMessage('Document category deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        console.error('Error deleting document category:', error.response?.data || error.message);
        setMessage(error.response?.data?.error || 'Error deleting document category');
      }
    }
  };  

  const filteredData = data.filter(category =>
    category.name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <CategoryIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Kategori Dokumen
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola kategori dokumen dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={() => window.location.reload()}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Kategori
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari Kategori"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Kategori
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data kategori...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Nama Kategori
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((category, index) => (
                      <TableRow 
                        key={category._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {category.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: category.is_active ? 'success.light' : 'error.light',
                            color: category.is_active ? 'success.dark' : 'error.dark',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {category.is_active ? 'Aktif' : 'Tidak Aktif'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => handleOpenDialog(category)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteClick(category._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>

      {/* Snackbar for Success Message */}
      <Snackbar
        open={successPopup}
        autoHideDuration={6000}
        onClose={() => setSuccessPopup(false)}
        message={message}
      />

      {/* Dialog for adding/editing document categories */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{selectedCategory ? 'Edit Document Category' : 'Add Document Category'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Category Name"
                variant="outlined"
                fullWidth
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                error={!!errors.categoryName}
                helperText={errors.categoryName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                  label="Status"
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveCategory} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentCategoryPage;
